import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";

const API_URL = process.env.REACT_APP_API_URL;

// Thunk para obtener todas las recomendaciones
export const fetchRecommendations = createAsyncThunk(
  "recommendations/fetchRecommendations",
  async (params = {}, { rejectWithValue, getState }) => {
    try {
      // Obtenemos el token del estado de autenticación
      const { token } = getState().auth;
      
      if (!token) {
        return rejectWithValue("No hay token de autenticación");
      }
      
      // Configuramos parámetros de paginación
      const { page = 1, limit = 10 } = params;
      
      const config = {
        headers: {
          Authorization: `Bearer ${token}`
        },
        params: {
          page,
          limit
        }
      };
      
      const response = await axios.get(`${API_URL}/api/v1/recommendations`, config);
      
      // Si el backend ya devuelve la estructura paginada
      if (response.data.pagination) {
        return {
          data: response.data.data,
          pagination: response.data.pagination
        };
      }
      
      // Si el backend aún no soporta paginación, simular paginación en el frontend
      const allData = response.data;
      const totalItems = allData.length;
      const totalPages = Math.ceil(totalItems / limit);
      const startIndex = (page - 1) * limit;
      const endIndex = Math.min(startIndex + limit, totalItems);
      
      return {
        data: allData.slice(startIndex, endIndex),
        pagination: {
          totalItems,
          totalPages,
          currentPage: page,
          itemsPerPage: limit
        }
      };
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Error al obtener recomendaciones"
      );
    }
  }
);

// Thunk para actualizar el estado de una recomendación
export const updateRecommendationStatus = createAsyncThunk(
  "recommendations/updateStatus",
  async ({ id, status }, { rejectWithValue, getState }) => {
    try {
      // Obtenemos el token del estado de autenticación
      const { token } = getState().auth;
      
      if (!token) {
        return rejectWithValue("No hay token de autenticación");
      }
      
      const config = {
        headers: {
          Authorization: `Bearer ${token}`
        }
      };
      
      const response = await axios.put(
        `${API_URL}/api/v1/recommendations/${id}/status`,
        { status },
        config
      );
      
      return { id, status, response: response.data };
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Error al actualizar el estado"
      );
    }
  }
);

const initialState = {
  recommendations: [],
  loading: false,
  error: null,
  pagination: {
    totalItems: 0,
    totalPages: 1,
    currentPage: 1,
    itemsPerPage: 10
  }
};

const recommendationsSlice = createSlice({
  name: "recommendations",
  initialState,
  reducers: {
    // Reducers adicionales si son necesarios
  },
  extraReducers: (builder) => {
    builder
      // Manejo de fetchRecommendations
      .addCase(fetchRecommendations.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchRecommendations.fulfilled, (state, action) => {
        state.loading = false;
        state.recommendations = action.payload.data;
        state.pagination = action.payload.pagination || {
          totalItems: action.payload.data.length,
          totalPages: 1,
          currentPage: 1,
          itemsPerPage: 10
        };
      })
      .addCase(fetchRecommendations.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      
      // Manejo de updateRecommendationStatus
      .addCase(updateRecommendationStatus.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateRecommendationStatus.fulfilled, (state, action) => {
        state.loading = false;
        // Actualizar el estado de la recomendación en el array
        const index = state.recommendations.findIndex(
          (rec) => rec.id === action.payload.id
        );
        if (index !== -1) {
          state.recommendations[index].status = action.payload.status;
        }
      })
      .addCase(updateRecommendationStatus.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export default recommendationsSlice.reducer;
